import { useState, useEffect } from "react";
import { X, Check, VolumeX, RotateCcw, Loader2, Copy, Clock } from "lucide-react";
import api from "../../services/api";

var SEV_CLS = {
  critical: "bg-red-100 text-red-700 border-red-200",
  high: "bg-orange-100 text-orange-700 border-orange-200",
  medium: "bg-amber-100 text-amber-700 border-amber-200",
  low: "bg-gray-100 text-gray-600 border-gray-200",
};

function fmtDate(d) {
  if (!d) return "-";
  try { return new Date(d).toLocaleString("pt-BR"); } catch(e) { return "-"; }
}

function InfoRow({ label, value }) {
  return (
    <div>
      <span className="text-[10px] font-medium text-gray-400 uppercase">{label}</span>
      <p className="text-sm text-[#0A2F3D] font-medium truncate">{String(value)}</p>
    </div>
  );
}

function ActBtn({ icon: Icon, label, color, busy, onClick, tid }) {
  return (
    <button onClick={onClick} disabled={busy} className={"flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-white rounded-lg disabled:opacity-50 " + color} data-testid={tid}>
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />}{label}
    </button>
  );
}

export default function VigiaEventDetail({ eventId, onClose, onAction }) {
  var evState = useState(null);
  var ev = evState[0];
  var setEv = evState[1];
  var ldState = useState(true);
  var ld = ldState[0];
  var setLd = ldState[1];
  var actState = useState(null);
  var act = actState[0];
  var setAct = actState[1];

  useEffect(function() {
    setLd(true);
    api.get("/vigia/events/" + eventId).then(function(r) { setEv(r.data); }).catch(function(){}).finally(function() { setLd(false); });
  }, [eventId]);

  function doAct(a, p) {
    setAct(a);
    var url = "/vigia/events/" + eventId + "/" + a;
    if (p) url += "?" + new URLSearchParams(p).toString();
    api.patch(url).then(function() { if (onAction) onAction(); }).catch(function(){}).finally(function() { setAct(null); });
  }

  if (ld || !ev) return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-2xl p-8"><div className="w-6 h-6 border-2 border-[#3B82F6] border-t-transparent rounded-full animate-spin mx-auto" /></div>
    </div>
  );

  var sc = SEV_CLS[ev.severity] || SEV_CLS.medium;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="vigia-detail-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2 min-w-0">
            <span className={"px-2 py-1 text-xs font-bold uppercase rounded border flex-shrink-0 " + sc}>{ev.severity}</span>
            <h2 className="text-sm font-bold text-[#0A2F3D] truncate">{ev.title}</h2>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg flex-shrink-0" data-testid="close-vigia-detail"><X className="w-5 h-5 text-gray-500" /></button>
        </div>
        <DetailBody ev={ev} act={act} doAct={doAct} />
      </div>
    </div>
  );
}

function DetailBody({ ev, act, doAct }) {
  var showOpen = ev.status === "open";
  var showAck = ev.status === "acknowledged";
  var showClosed = ev.status === "resolved" || ev.status === "muted";

  return (
    <div className="p-6 space-y-4">
      <div className="flex flex-wrap gap-2">
        {showOpen && <ActBtn icon={Check} label="Reconhecer" color="bg-blue-500" busy={act === "acknowledge"} onClick={function() { doAct("acknowledge"); }} tid="vigia-ack-btn" />}
        {(showOpen || showAck) && <ActBtn icon={Check} label="Resolver" color="bg-green-500" busy={act === "resolve"} onClick={function() { doAct("resolve"); }} tid="vigia-resolve-btn" />}
        {(showOpen || showAck) && <ActBtn icon={VolumeX} label="Silenciar 24h" color="bg-gray-500" busy={act === "mute"} onClick={function() { doAct("mute", { hours: 24 }); }} tid="vigia-mute-btn" />}
        {showClosed && <ActBtn icon={RotateCcw} label="Reabrir" color="bg-amber-500" busy={act === "reopen"} onClick={function() { doAct("reopen"); }} tid="vigia-reopen-btn" />}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <InfoRow label="Status" value={ev.status} />
        <InfoRow label="Origem" value={ev.source} />
        <InfoRow label="Endpoint" value={ev.endpoint || "-"} />
        <InfoRow label="Metodo" value={ev.method || "-"} />
        <InfoRow label="Modulo" value={ev.module || "-"} />
        <InfoRow label="Tipo" value={ev.error_type || "-"} />
        <InfoRow label="Ocorrencias" value={ev.occurrence_count + "x"} />
        <InfoRow label="Status Code" value={ev.last_status_code || "-"} />
        <InfoRow label="Primeira vez" value={fmtDate(ev.first_seen_at)} />
        <InfoRow label="Ultima vez" value={fmtDate(ev.last_seen_at)} />
        <InfoRow label="Ultimo email" value={fmtDate(ev.last_notified_at)} />
        <InfoRow label="Resolvido em" value={fmtDate(ev.resolved_at)} />
      </div>

      <div>
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-medium text-gray-400">FINGERPRINT</span>
          <button onClick={function() { navigator.clipboard.writeText(ev.fingerprint); }} className="p-0.5 hover:bg-gray-100 rounded"><Copy className="w-3 h-3 text-gray-400" /></button>
        </div>
        <p className="text-xs font-mono text-gray-500 bg-gray-50 px-3 py-2 rounded-lg break-all">{ev.fingerprint}</p>
      </div>

      <div>
        <span className="text-xs font-medium text-gray-400">MENSAGEM</span>
        <p className="text-sm text-gray-700 bg-gray-50 px-3 py-2 rounded-lg mt-1 whitespace-pre-wrap break-words font-mono">{ev.message}</p>
      </div>

      {ev.stack_trace && (
        <div>
          <span className="text-xs font-medium text-gray-400">STACK TRACE</span>
          <pre className="text-[11px] bg-[#0A2F3D] text-green-300 px-4 py-3 rounded-lg mt-1 overflow-x-auto max-h-48 whitespace-pre-wrap">{ev.stack_trace}</pre>
        </div>
      )}

      <ContextTimeline contexts={ev.recent_contexts} />
    </div>
  );
}

function ContextTimeline({ contexts }) {
  if (!contexts || contexts.length === 0) return null;
  return (
    <div>
      <span className="text-xs font-medium text-gray-400">TIMELINE ({contexts.length})</span>
      <div className="mt-2 space-y-2 max-h-48 overflow-y-auto">
        {contexts.slice().reverse().map(function(ctx, i) {
          return (
            <div key={i} className="flex items-start gap-2 text-xs text-gray-500 bg-gray-50 px-3 py-2 rounded-lg">
              <Clock className="w-3 h-3 mt-0.5 flex-shrink-0" />
              <div>
                <span className="font-medium">{fmtDate(ctx.timestamp)}</span>
                {ctx.user_name && <span className="ml-2 text-gray-400">({ctx.user_name})</span>}
                <p className="text-gray-600 mt-0.5 truncate max-w-[400px]">{ctx.message}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
